import { useEffect, useReducer } from "react";
import { initialState, reducer } from "./state";
import { useToast } from "@chakra-ui/react";
import { Project } from "models/project_type";
import axios from "axios";
import { UserModelGet } from "models/user_model";
import { CreateTaskCard, defaultCols, TaskCard } from "models/tasks_type";
import { ActionTypeReducer } from "helpers/types";
import { generateId, validatorEngine } from "helpers/functions";

type changeInputType = ActionTypeReducer<typeof initialState.taskForm>

export const useLaunchedProject = () => {
    const toast = useToast({
        position: "top-right",
        duration: 3000,
    })

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        loadData();
    }, []);

    const loadOrder = async () => {
        const userData = JSON.parse(localStorage.getItem("user"));
        const url = process.env.REACT_APP_API_URL + `/projeto/list/${userData?.idEmpresa}`;
        return axios.get<Project[]>(url);
    };

    const getLoggedUser = () => {
        const userData = JSON.parse(localStorage.getItem("user"));
        dispatch({ type: "setLoggedUser", payload: userData });

        return userData as UserModelGet;
    }

    const loadUsers = () => {
        const user = state.loggedUser ?? getLoggedUser();

        return axios.get<UserModelGet[]>(
            process.env.REACT_APP_API_URL + "/user/list/" + user.idEmpresa
        );
    }

    const loadTasks = async (projectId: number) => {
        const url = process.env.REACT_APP_API_URL + `/projeto/task/${projectId}`;
        return axios.get<TaskCard[]>(url);
    };

    const loadData = async () => {
        dispatch({ type: "setLoading", payload: true });

        try {
            const [users, orders] = await Promise.all([loadUsers(), loadOrder()]);

            dispatch({ type: "setUsers", payload: users.data });
            dispatch({ type: "setTableList", payload: orders.data });
        } catch (e) {
            console.error("useEffect.loadData ", e);
            showError({ description: "Erro ao carregar dados" });
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    }

    const changeInput = ({ type, payload }: changeInputType) => {
        const company = { ...state.taskForm, [type]: payload };
        dispatch({ type: "setTaskForm", payload: company });
    }

    const getTasksList = async (project: Project) => {
        dispatch({ type: "setLoading", payload: true });

        try {
            const tasks = await loadTasks(project.id);

            dispatch({ type: "setProjectSelected", payload: project });
            dispatch({ type: "setTasksList", payload: tasks.data });
        } catch (e) {
            console.error("getTasksList ", e);
            showError({ description: "Erro ao carregar tarefas" });
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    };

    const validateCompanyFields = () => {
        const { taskForm } = state;
        const validations: validatorEngine.type<typeof initialState.taskFormFieldsError> = {
            'titulo': {
                test: () => validatorEngine.testString('minLength', taskForm.titulo, 8),
                errorMessage: "Campo precisa ter mais de 8 caracteres",
            },
            'descricao': {
                test: () => validatorEngine.testString('minLength', taskForm.descricao, 8),
                errorMessage: "Campo precisa ter mais de 8 caracteres",
            },
            'dataEstimativa': {
                test: () => taskForm.dataEstimativa > new Date(),
                errorMessage: "Data precisa ser maior que a data atual",
            },
            'userResponsavelId': {
                test: () => validatorEngine.testString('required', taskForm.userResponsavelId),
                errorMessage: "Campo obrigatório",
            },
            'status': {
                test: () => defaultCols.map(col => col.id).includes(taskForm.status as typeof defaultCols[number]['id']),
                errorMessage: "Campo obrigatório",
            },
        };

        const { isValid, errors } = validatorEngine.build(validations);

        dispatch({
            type: "setTaskFormFieldsError",
            payload: { ...state.taskFormFieldsError, ...errors }
        });

        return isValid;
    };

    const postTask = async (task?: CreateTaskCard) => {
        const payload = task ?? state.taskForm;
        const url = process.env.REACT_APP_API_URL + "/projeto/task";
        const user = state.loggedUser ?? getLoggedUser();

        return axios.post(url, {
            ...payload,
            userCriadorId: user.id,
            projetoId: state.projectSelected.id,
        });
    };

    const saveTask = async () => {
        if (!validateCompanyFields()) return;

        dispatch({ type: "setLoading", payload: true });

        try {
            await postTask();

            setShowTaskForm(false);
            await getTasksList(state.projectSelected);
        } catch (e) {
            console.error("useNewCompany.saveCompany ", e);
            showError({ description: "Erro ao salvar tarefa" });
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    };

    const resetForm = () => {
        dispatch({ type: "setTaskForm", payload: { ...initialState.taskForm, code: generateId() } });
        dispatch({ type: "setTaskFormFieldsError", payload: initialState.taskFormFieldsError });
    };

    const setShowTaskForm = (show: boolean, item?: TaskCard) => {
        if (!show) resetForm();

        if (show && item) {
            const itemPass: typeof initialState.taskForm = {
                id: item.id,
                code: item.code,
                titulo: item.titulo,
                dataCriacao: new Date(item.dataCriacao),
                dataEstimativa: new Date(item.dataEstimativa),
                descricao: item.descricao,
                projetoId: item.projetoId,
                status: item.status,
                userCriadorId: item.userCriadorId,
                userResponsavelId: item.userResponsavelId,
            }

            dispatch({ type: "setTaskForm", payload: itemPass });
        }

        dispatch({ type: "setShowTaskForm", payload: show });
    };

    const changeTaskStatus = async (item: TaskCard, status: string) => {
        const payload: typeof initialState.taskForm = {
            id: item.id,
            code: item.id,
            titulo: item.titulo,
            dataCriacao: new Date(item.dataCriacao),
            dataEstimativa: new Date(item.dataEstimativa),
            descricao: item.descricao,
            projetoId: item.projetoId,
            status: status,
            userCriadorId: item.userCriadorId,
            userResponsavelId: item.userResponsavelId,
        }

        toast.promise(postTask(payload), {
            success: {
                title: 'Sucesso',
                description: 'OS\'s salvas com sucesso.',
            },
            error: {
                title: 'Erro',
                description: 'Houve um problema, tente mais tarde.',
            },
            loading: {
                title: 'Carregando',
                description: 'Por favor aguarde...',
            },
        });
    };

    const closeKamban = () => {
        resetForm();
        dispatch({ type: "setProjectSelected", payload: null });
        dispatch({ type: "setTasksList", payload: [] });
    };

    const displayInputError = (type: keyof typeof initialState.taskFormFieldsError) => {
        const error = state.taskFormFieldsError[type];

        return ({
            error: !!error,
            errorMessage: error
        })
    }

    const showError = ({ title = "Erro!", description = "Houve um problema, tente mais tarde!" }) => {
        toast({
            title,
            description,
            status: "error",
            isClosable: true,
        });
    };

    return {
        state,
        showError,
        getTasksList,
        closeKamban,
        changeInput,
        setShowTaskForm,
        saveTask,
        changeTaskStatus,
        displayInputError,
    };
};

export namespace useLaunchedProject {
    export type type = ReturnType<typeof useLaunchedProject>;
}
